import { useMemo } from 'react';
import { ExternalLink, FileSearch, FileText } from 'lucide-react';

import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import {
  buildCandidateProposalPagePath,
  buildCandidateProposalTopicPagePath,
  buildCandidateProposalTopicPath,
} from '@/utils/candidateProposalRoute';
import type { Candidate } from '@/models/candidate';
import type { Proposal, ProposalReference } from '@/models/proposal';
import type { Topic } from '@/models/topic';
import { classNames } from '@/utils/classNames';

import styles from '@/styles/positions.module.css';

type SourceDetailsProps = {
  candidate: Candidate;
  topic: Topic;
  proposal: Proposal;
  className?: string;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function compactDocumentTitle(documentTitle: string, candidateName: string): string {
  const suffixPattern = new RegExp(`\\s+(de|do|da|dos|das)\\s+${escapeRegExp(candidateName)}$`, 'i');
  const directPattern = new RegExp(`\\s+${escapeRegExp(candidateName)}$`, 'i');

  return documentTitle.replace(suffixPattern, '').replace(directPattern, '').trim() || documentTitle;
}

function formatReferencePage(reference: ProposalReference): string {
  if (!reference.pageStart) {
    return 'Trecho consultado';
  }

  if (reference.pageEnd && reference.pageEnd !== reference.pageStart) {
    return `p. ${reference.pageStart}-${reference.pageEnd}`;
  }

  return `p. ${reference.pageStart}`;
}

function TopicIcon({ icon: Icon }: { icon: Topic['icon'] }): JSX.Element {
  return <Icon size={28} strokeWidth={2.1} />;
}

export function SourceDetails({ candidate, topic, proposal, className }: SourceDetailsProps): JSX.Element {
  const proposalPath = buildCandidateProposalPagePath(candidate.id);
  const topicProposalPath = buildCandidateProposalTopicPath(candidate.id, topic.label);
  const visibleReferences = useMemo(() => proposal.references.slice(0, 4), [proposal.references]);
  const hiddenReferencesCount = proposal.references.length - visibleReferences.length;
  const hasReferences = proposal.references.length > 0;

  if (proposal.status === 'not_found') {
    return (
      <EmptyState
        className={className}
        tone="warning"
        icon={<FileSearch size={20} />}
        title="Nenhum resumo consolidado encontrado"
        description={`Não encontramos um resumo consolidado sobre ${topic.missingFocus}. Consulte o documento completo para checar menções ao tema.`}
        action={
          <Button to={proposalPath} variant="secondary">
            <ExternalLink size={14} />
            Abrir proposta completa
          </Button>
        }
      />
    );
  }

  return (
    <div className={classNames(styles.sourceDetails, className)}>
      <Card tone="soft" className={styles.sourceSummaryCard}>
        <div className={styles.sourceSummaryLayout}>
          <div className={styles.sourceSummaryIcon} aria-hidden="true">
            <TopicIcon icon={topic.icon} />
          </div>

          <div className={styles.sourceSummaryCopy}>
            <p className={styles.sourceSummaryEyebrow}>Síntese das propostas sobre {topic.label}</p>
            <p className={styles.sourceSummaryText}>{proposal.summary}</p>
          </div>
        </div>

        <div className={styles.sourceMetaRow}>
          <div className={styles.sourceSummaryMeta}>
            <FileText size={16} />
            <span>
              {hasReferences
                ? `Síntese elaborada a partir de ${proposal.references.length} ${proposal.references.length === 1 ? 'trecho' : 'trechos'} da proposta.`
                : 'Síntese consolidada a partir do documento consultado.'}
            </span>
          </div>

          {hasReferences ? (
            <Button href="#fontes-consultadas" variant="ghost" size="sm">
              Ver como essa síntese foi construída
            </Button>
          ) : null}
        </div>
      </Card>

      {proposal.references.length > 0 ? (
        <details className={styles.sourceSourcesDetails} id="fontes-consultadas">
          <summary className={styles.sourceSourcesSummary} aria-label="Expandir fontes consultadas">
            <span>
              <span className={styles.sourceSectionTitle}>Fontes consultadas</span>
              <span className={styles.sourceSourcesSummaryMeta}>{proposal.references.length} trechos</span>
            </span>
            <span className={styles.sourceSourcesSummaryHint}>Abrir</span>
          </summary>

          <section className={styles.sourceSourcesSection} aria-label="Trechos consultados">
            <p className={styles.sourceSectionSubtitle}>Trechos usados para montar a síntese.</p>

            <div className={styles.sourceReferenceGrid}>
              {visibleReferences.map((reference) => {
                const documentLabel = compactDocumentTitle(reference.documentTitle, candidate.name);

                return (
                  <Card key={reference.proposalId} tone="soft" className={styles.sourceReferenceCard}>
                    <div className={styles.sourceReferenceTop}>
                      <p className={styles.sourceReferenceMeta}>
                        {documentLabel} • {formatReferencePage(reference)}
                      </p>

                      <Button to={buildCandidateProposalPagePath(candidate.id, reference.pageStart)} variant="ghost" size="sm">
                        Ver trecho completo
                      </Button>
                      <Button to={buildCandidateProposalTopicPagePath(candidate.id, topic.label, reference.pageStart)} variant="ghost" size="sm">
                        Ver trecho por tema
                      </Button>
                    </div>

                    <p className={styles.sourceReferenceExcerpt}>{reference.excerpt ?? 'Trecho consultado no documento original.'}</p>
                  </Card>
                );
              })}
            </div>

            {hiddenReferencesCount > 0 ? (
              <p className={styles.sourceReferenceMore}>
                +{hiddenReferencesCount} {hiddenReferencesCount === 1 ? 'trecho adicional' : 'trechos adicionais'} no documento completo.
              </p>
            ) : null}
          </section>
        </details>
      ) : null}

      <div className={styles.sourceActions}>
        <Button to={proposalPath} variant="secondary">
          <ExternalLink size={14} />
          Ver proposta original completa
        </Button>
        <Button to={topicProposalPath} variant="ghost">
          <ExternalLink size={14} />
          Ver proposta por tema
        </Button>
      </div>
    </div>
  );
}
